import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Dices, Lock, CheckCircle2, Shuffle, ArrowDown, ArrowUp, MapPin } from 'lucide-react';
import {
  findViolations,
  groupLayout,
  interClubMatchCount,
  partnerLayoutSpec,
  partnerMatchCount,
  phaseStandings,
  roundRobinMatchCount,
  tierMatchCount,
  type Group,
  type GroupsConfig,
  type Match,
  type Phase,
  type StandingRow,
} from '@airmail/engine';
import { useCompetition, useTurnier } from '@/store/TournamentProvider';
import { Button, Card, Dialog, Empty, Pill, SectionHead, cx } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { usePhaseAbschluss } from '@/lib/phaseAbschluss';
import { useT } from '@/i18n';
import { useTm } from '@/i18n/engine';
import { StandingsTable } from '@/components/StandingsTable';
import { MatchRow } from '@/components/MatchCard';
import { ResultDialog } from '@/components/ResultDialog';
import { teamLookup } from '@/lib/teams';
import { fmtMinutes } from '@/lib/format';

/*
 * Hinweise der Auslosung bleiben stehen (Harald, 10/2026: „rechts unten
 * erscheinen Anmerkungen, die sehr hilfreich wären — sie verschwinden aber
 * nach wenigen Sekunden"). Gemerkt je Phase im Browser, bis zum nächsten
 * Auslosen/Verschieben oder bis man sie wegklickt.
 */
function useAuslosungHinweise(phaseId: string | undefined) {
  const schluessel = `airmail.auslosung.${phaseId ?? ''}`;
  const [hinweise, setRoh] = useState<string[]>(() => {
    try {
      const roh = phaseId ? localStorage.getItem(schluessel) : null;
      return roh ? (JSON.parse(roh) as string[]) : [];
    } catch {
      return [];
    }
  });
  const setHinweise = (liste: string[]) => {
    setRoh(liste);
    try {
      if (liste.length) localStorage.setItem(schluessel, JSON.stringify(liste));
      else localStorage.removeItem(schluessel);
    } catch {
      /* privates Fenster — gilt bis zum Neuladen */
    }
  };
  const mitHinweisen = (r: { ok: boolean; warnings: string[] }) => {
    if (r.ok) setHinweise(r.warnings);
  };
  return { hinweise, setHinweise, mitHinweisen };
}

export function VorrundePage() {
  const t = useT();
  const tm = useTm();
  const { tournament, dispatch } = useTurnier();
  const comp = useCompetition();
  const phase = comp.phases.find((p) => p.type === 'groups');
  const lookup = teamLookup(comp);
  const [dragTeam, setDragTeam] = useState<string | null>(null);
  const [lotGroup, setLotGroup] = useState<Group | null>(null);
  const [resultMatch, setResultMatch] = useState<Match | null>(null);
  const [showMatches, setShowMatches] = useState(false);
  const { hinweise, setHinweise, mitHinweisen } = useAuslosungHinweise(phase?.id);

  const cfg = phase?.config as GroupsConfig | undefined;
  // Plätze (docs/17): Feldgruppen, denen eine Gruppe fest zugeordnet werden kann
  const courtGroups = [...new Set(tournament.courts.filter((c) => c.active && c.group).map((c) => c.group as string))].sort((a, b) => a.localeCompare(b));
  const gemeldet = comp.teams.filter((t) => t.status === 'registered' || t.status === 'confirmed');
  // „Nur eingecheckte Teams auslosen" (docs/13 §4): der Kader ist komplett drin, gespielt wird mit denen, die da sind
  const nurEingecheckte = !!cfg?.checkedInOnly;
  const eligible = nurEingecheckte ? gemeldet.filter((t) => t.checkedIn) : gemeldet;
  // Wechselnde Partner: Gruppengröße als Untergrenze, Spiele nach Partner-Plan
  const layout = cfg ? groupLayout(eligible.length, cfg.partners && 'groupSize' in cfg.layout ? partnerLayoutSpec(eligible.length, cfg.layout.groupSize) : cfg.layout) : undefined;
  const clubSizes = Object.values(eligible.reduce<Record<string, number>>((acc, t) => ({ ...acc, [t.club ?? '']: (acc[t.club ?? ''] ?? 0) + 1 }), {}));
  // Leistungsgruppen (docs/13 §7): je Nummer im Teamnamen eine Gruppe
  const stufen = cfg?.groupByTeamNumber ? tierMatchCount(eligible) : undefined;
  const plannedMatches = cfg?.noIntraClubMatches
    ? stufen
      ? stufen.matches
      : interClubMatchCount(clubSizes)
    : (layout?.sizes.reduce((s, n) => s + (cfg?.partners ? partnerMatchCount(n, cfg.partners) : roundRobinMatchCount(n)), 0) ?? 0);
  const tables = phase ? phaseStandings(tournament, phase.id) : [];
  const violations = useMemo(() => {
    if (!phase || !cfg) return [];
    const clubOf = (id: string) => lookup(id)?.club;
    return findViolations(
      phase.groups.map((g) => g.teamIds),
      { clubOf, avoidSameClub: cfg.constraints.avoidSameClub, keepApart: cfg.constraints.keepApart },
    );
  }, [phase, cfg, lookup]);

  if (!phase || !cfg) {
    return (
      <div className="max-w-2xl">
        <SectionHead title={t('Auslosung')} />
        <Empty title={t('Keine Gruppenphase im Modus')}>
          {t('Der Modus dieses Turniers hat keine Vorrunde. Unter')}{' '}
          <Link to="../einstellungen" className="text-blue underline">
            {t('Einstellungen')}
          </Link>{' '}
          {t('auf „Gruppen → KO" oder „Jeder gegen Jeden" wechseln.')}
        </Empty>
      </div>
    );
  }

  const editable = (phase.status === 'drawn' || phase.status === 'planned') && !phase.frozen;
  const hasResults = phase.matches.some((m) => m.status === 'finished' || m.status === 'walkover' || m.status === 'running');
  const canMove = editable && !hasResults;
  const finished = phase.matches.filter((m) => m.status === 'finished' || m.status === 'walkover').length;
  const total = phase.matches.length;

  const draw = async () => {
    if (phase.status === 'drawn' && !(await confirmDialog(t('Neu auslosen? Die aktuelle Gruppeneinteilung wird verworfen.'), { ok: t('Neu auslosen'), danger: true }))) return;
    mitHinweisen(dispatch({ type: 'phase.draw', phaseId: phase.id }));
  };

  // Sichtbar bis zum Einfrieren: so würfelt „Neu würfeln" auch nur mit Eingecheckten (Daniel, 10/2026)
  const checkinSchalter = (
    <label
      className={cx('inline-flex items-center gap-1.5 text-xs cursor-pointer', nurEingecheckte ? 'text-ink' : 'text-dim')}
      title={t('Der ganze Kader bleibt im Turnier; ausgelost werden nur Teams mit Check-in. Wer später eincheckt, bekommt seine Spiele sofort.')}
    >
      <input type="checkbox" checked={nurEingecheckte} onChange={(e) => dispatch({ type: 'phase.setOptions', phaseId: phase.id, patch: { checkedInOnly: e.target.checked } })} data-nur-eingecheckte />{' '}
      {t('nur eingecheckte Teams auslosen')}
    </label>
  );

  return (
    <div>
      <SectionHead
        title={t('Auslosung')}
        meta={
          phase.status === 'planned'
            ? `${cfg.noIntraClubMatches ? t('{n} Paare aus {m} Mannschaften', { n: eligible.length, m: clubSizes.length }) : t('{n} Teams', { n: eligible.length })}${nurEingecheckte ? ` (${t('{a} von {b} eingecheckt', { a: eligible.length, b: gemeldet.length })})` : ''} · ${cfg.noIntraClubMatches ? (stufen ? t('{n} Leistungsgruppen nach Nummer', { n: stufen.groups }) : t('jedes Paar gegen alle anderen Mannschaften')) : tm(layout?.summary ?? '')} · ${t('{n} Spiele', { n: plannedMatches })} · ${t('ca. {d}', { d: fmtMinutes((plannedMatches * (comp.ruleSet.plannedMatchMinutes + 2)) / Math.max(1, tournament.courts.filter((c) => c.active).length)) })}`
            : `${t('{n} Gruppen', { n: phase.groups.length })} · ${t('{n} von {m} Spielen gewertet', { n: finished, m: total })}${phase.frozen ? ` · ${t('eingefroren')}` : ''}`
        }
      >
        {phase.status === 'planned' && (
          <>
            {cfg.noIntraClubMatches && (
              <label
                className={cx('inline-flex items-center gap-1.5 text-xs cursor-pointer', cfg.groupByTeamNumber ? 'text-ink' : 'text-dim')}
                title={t(
                  '„Ceva 1“, „Nucetto 1“ … spielen in Gruppe 1 jeder gegen jeden, die Zweier in Gruppe 2 — wie Medenspiele. Aus: eine Gruppe, jedes Paar gegen alle Paare der anderen Mannschaften.',
                )}
              >
                <input
                  type="checkbox"
                  checked={!!cfg.groupByTeamNumber}
                  onChange={(e) => dispatch({ type: 'phase.updateConfig', phaseId: phase.id, config: { ...cfg, groupByTeamNumber: e.target.checked } })}
                  data-leistungsgruppen
                />{' '}
                {t('Leistungsgruppen (gleiche Nummer)')}
              </label>
            )}
            {checkinSchalter}
            <Button
              variant="primary"
              onClick={draw}
              disabled={eligible.length < 2}
              title={eligible.length < 2 ? (nurEingecheckte ? t('Erst einchecken: Teams → Check-in') : t('Mindestens zwei Teams nötig')) : undefined}
            >
              <Dices size={14} /> {t('Auslosen')}
            </Button>
          </>
        )}
        {phase.status === 'drawn' && !phase.frozen && (
          <>
            {checkinSchalter}
            <Button onClick={draw}>
              <Shuffle size={14} /> {t('Neu würfeln')}
            </Button>
            <Button variant="primary" onClick={() => dispatch({ type: 'phase.freeze', phaseId: phase.id })}>
              <Lock size={14} /> {t('Einfrieren')}
            </Button>
          </>
        )}
        {(phase.status === 'drawn' || phase.status === 'running') && (
          <>
            <Button variant="ghost" onClick={() => setShowMatches(!showMatches)}>
              {showMatches ? t('Tabellen zeigen') : t('Spiele zeigen')}
            </Button>
            <AbschlussKnopf phase={phase} ligaModus={!!cfg.noIntraClubMatches} />
          </>
        )}
        {phase.status === 'finished' && <Pill tone="blue">{t('abgeschlossen')}</Pill>}
      </SectionHead>

      {hinweise.length > 0 && phase.status !== 'planned' && (
        <div className="border border-warn/60 bg-warn/10 px-4 py-3 mb-4 text-sm" role="status" data-auslosung-hinweise>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="label text-warn">{t('Hinweise zur Auslosung')}</span>
            <button type="button" className="ml-auto text-dim hover:text-ink cursor-pointer text-xs" onClick={() => setHinweise([])}>
              {t('Ausblenden')}
            </button>
          </div>
          <ul className="m-0 pl-4 grid gap-0.5">
            {hinweise.map((h) => (
              <li key={h}>{tm(h)}</li>
            ))}
          </ul>
        </div>
      )}

      {phase.status === 'planned' && (
        <Empty title={t('Noch nicht ausgelost')}>
          {eligible.length < 2
            ? nurEingecheckte && gemeldet.length >= 2
              ? t('{n} Teams gemeldet, {a} eingecheckt. Ausgelost werden nur eingecheckte Teams — unter „Teams“ einchecken (Häkchen oder Check-in-Modus), oder den Schalter oben ausstellen.', {
                  n: gemeldet.length,
                  a: eligible.length,
                })
              : t('Mindestens 2 Teams anlegen.')
            : stufen
              ? t(
                  'Auslosen bildet {g} Leistungsgruppen nach der Nummer im Teamnamen — „Ceva 1“, „Nucetto 1“ … in Gruppe 1 — und erzeugt darin alle Spiele zwischen verschiedenen Mannschaften ({n}).',
                  {
                    g: stufen.groups,
                    n: plannedMatches,
                  },
                )
              : cfg.noIntraClubMatches
                ? t('Auslosen erzeugt alle Spiele zwischen Paaren verschiedener Mannschaften ({n}). Reihenfolge und Felder werden am Spieltag fair verteilt — kein Paar spielt ständig.', {
                    n: plannedMatches,
                  })
                : cfg.constraints.avoidSameClub
                  ? t('Auslosen verteilt {n} Teams nach {d}, Vereinskollegen getrennt. Danach kannst du Teams per Drag & Drop verschieben und neu würfeln — bis du einfrierst.', {
                      n: eligible.length,
                      d: cfg.distribution === 'snake' ? t('Schlangensystem') : cfg.distribution === 'pots' ? t('Töpfen') : t('Zufall'),
                    })
                  : t('Auslosen verteilt {n} Teams nach {d}. Danach kannst du Teams per Drag & Drop verschieben und neu würfeln — bis du einfrierst.', {
                      n: eligible.length,
                      d: cfg.distribution === 'snake' ? t('Schlangensystem') : cfg.distribution === 'pots' ? t('Töpfen') : t('Zufall'),
                    })}
        </Empty>
      )}

      {phase.status !== 'planned' && (
        <>
          {violations.length > 0 && (
            <div className="border border-warn text-warn text-sm px-3 py-2 mb-3">
              {violations.length > 1 ? t('{n} Verstöße gegen Nebenbedingungen:', { n: violations.length }) : t('{n} Verstoß gegen Nebenbedingungen:', { n: violations.length })}{' '}
              {violations
                .map((v) => `${lookup(v.teamIds[0])?.shortName} + ${lookup(v.teamIds[1])?.shortName} (${v.reason === 'sameClub' ? t('gleicher Verein') : t('sollen getrennt sein')})`)
                .join(' · ')}
            </div>
          )}
          {canMove && <div className="text-xs text-dim mb-3">{t('Teams per Drag & Drop oder mit „Verschieben nach …“ zwischen Gruppen verschieben. Spiele werden neu erzeugt.')}</div>}
          {courtGroups.length > 0 && phase.status !== 'finished' && (
            <div className="text-xs text-dim mb-3">
              <MapPin size={11} className="inline mr-1" />
              {t('Plätze: {p} — jeder Gruppe einen Platz zuweisen, dann laufen ihre Spiele nur auf den Feldern dort (Turnierleitung Platz 2 filtert unter „Spiele“ auf ihren Platz).', {
                p: courtGroups.join(', '),
              })}
            </div>
          )}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(360px,100%),1fr))] gap-3">
            {phase.groups.map((g) => {
              const table = tables.find((t) => t.group.id === g.id);
              const gMatches = phase.matches.filter((m) => m.groupId === g.id);
              const gDone = gMatches.filter((m) => m.status === 'finished' || m.status === 'walkover').length;
              return (
                <Card
                  key={g.id}
                  title={t('Gruppe {g}', { g: g.name })}
                  aside={
                    <>
                      {courtGroups.length > 0 && phase.status !== 'finished' && (
                        <select
                          className="bg-panel2 border border-line px-1.5 py-0.5 text-[11px] font-mono outline-none focus:border-blue"
                          value={g.courtGroup && courtGroups.includes(g.courtGroup) ? g.courtGroup : ''}
                          title={t('Platz dieser Gruppe')}
                          aria-label={t('Platz Gruppe {g}', { g: g.name })}
                          onChange={(e) => dispatch({ type: 'group.setCourtGroup', phaseId: phase.id, groupId: g.id, courtGroup: e.target.value || undefined })}
                        >
                          <option value="">{t('Platz: egal')}</option>
                          {courtGroups.map((cg) => (
                            <option key={cg} value={cg}>
                              {t('Platz {p}', { p: cg })}
                            </option>
                          ))}
                        </select>
                      )}
                      <span className="font-mono text-[11px] text-dim">
                        {t('{n} Teams', { n: g.teamIds.length })} · {t('{n}/{m} Spiele', { n: gDone, m: gMatches.length })}
                      </span>
                    </>
                  }
                  className={cx(dragTeam && canMove && 'border-dashed')}
                >
                  <div
                    className="p-3"
                    onDragOver={(e) => canMove && e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragTeam && canMove) mitHinweisen(dispatch({ type: 'phase.moveTeam', phaseId: phase.id, teamId: dragTeam, toGroupId: g.id }));
                      setDragTeam(null);
                    }}
                  >
                    {canMove ? (
                      <ul className="m-0 p-0 list-none grid gap-1">
                        {g.teamIds.map((id) => {
                          const team = lookup(id);
                          return (
                            <li
                              key={id}
                              draggable
                              onDragStart={() => setDragTeam(id)}
                              onDragEnd={() => setDragTeam(null)}
                              className="bg-raise border border-line px-3 py-1.5 text-sm cursor-grab active:cursor-grabbing flex items-center gap-2 flex-wrap"
                            >
                              <span className="font-bold tracking-tight">{team?.name}</span>
                              {team?.seed && <span className="label">{t('Seed {n}', { n: team.seed })}</span>}
                              {team?.club && <span className="text-dim text-xs">{team.club}</span>}
                              {/* Ziehen geht nur mit der Maus — am Handy und per Tastatur verschiebt diese Auswahl */}
                              {phase.groups.length > 1 && (
                                <select
                                  className="ml-auto bg-panel2 border border-field px-1.5 py-0.5 text-[11px] font-mono outline-none focus:border-blue cursor-pointer max-md:min-h-11"
                                  value=""
                                  aria-label={t('{team} verschieben nach …', { team: team?.name ?? '' })}
                                  data-verschieben={id}
                                  onChange={(e) => e.target.value && mitHinweisen(dispatch({ type: 'phase.moveTeam', phaseId: phase.id, teamId: id, toGroupId: e.target.value }))}
                                >
                                  <option value="">{t('Verschieben nach …')}</option>
                                  {phase.groups
                                    .filter((x) => x.id !== g.id)
                                    .map((x) => (
                                      <option key={x.id} value={x.id}>
                                        {t('Gruppe {g}', { g: x.name })}
                                      </option>
                                    ))}
                                </select>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : showMatches ? (
                      <div className="grid gap-1">
                        {gMatches.map((m) => (
                          <MatchRow
                            key={m.id}
                            match={m}
                            onClick={m.a.kind === 'team' && m.b.kind === 'team' && m.status !== 'cancelled' && phase.status !== 'finished' ? () => setResultMatch(m) : undefined}
                            right={<StatusPill status={m.status} />}
                          />
                        ))}
                      </div>
                    ) : (
                      table && (
                        <StandingsTable
                          rows={table.rows}
                          lookup={lookup}
                          qualifiers={cfg.qualifiersPerGroup}
                          compact
                          pending={gDone < gMatches.filter((m) => m.status !== 'cancelled').length}
                          onLot={() => setLotGroup(g)}
                        />
                      )
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {lotGroup && <LotDialog group={lotGroup} rows={tables.find((t) => t.group.id === lotGroup.id)?.rows ?? []} phaseId={phase.id} onClose={() => setLotGroup(null)} />}
      {resultMatch && <ResultDialog match={resultMatch} onClose={() => setResultMatch(null)} />}
    </div>
  );
}

/**
 * „Phase abschließen" (bzw. „Turnier beenden", wenn keine Phase folgt) — dieselbe
 * Logik wie unter „Spiele" (lib/phaseAbschluss). Die Seite bleibt danach stehen;
 * nur im Liga-Modus geht es zur Tabelle: Der Spieltag ist fertig — jetzt gehört
 * er in die Saison, sonst geht er unter (Harald hatte das schon vergessen). Dort
 * wartet „In Saison übernehmen" hervorgehoben (Tabelle.tsx). Nur das Gerät, das
 * abgeschlossen hat, springt — nicht jedes, das den Stand über die Cloud
 * bekommt — und ein Helfer nie: `abschliessen()` liefert für ihn `false`.
 */
function AbschlussKnopf({ phase, ligaModus }: { phase: Phase; ligaModus: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const a = usePhaseAbschluss(phase);
  const finish = async () => {
    if ((await a.abschliessen()) && ligaModus) void navigate(a.ziel);
  };
  return (
    <Button onClick={finish} disabled={a.gewertet === 0} title={a.gewertet === 0 ? t('Noch kein Spiel beendet') : undefined}>
      <CheckCircle2 size={14} /> {a.letztePhase ? t('Turnier beenden') : t('Phase abschließen')}
    </Button>
  );
}

export function StatusPill({ status }: { status: Match['status'] }) {
  const t = useT();
  const map: Record<Match['status'], [string, 'default' | 'live' | 'warn' | 'red' | 'blue']> = {
    open: [t('offen'), 'default'],
    ready: [t('bereit'), 'default'],
    running: [t('läuft'), 'live'],
    finished: [t('fertig'), 'blue'],
    walkover: [t('w.o.'), 'warn'],
    cancelled: [t('abgesagt'), 'red'],
  };
  const [label, tone] = map[status];
  return <Pill tone={tone}>{label}</Pill>;
}

/** Los-Dialog (docs/04 §5): manuell ordnen oder zufällig — immer eine bewusste Entscheidung */
function LotDialog({ group, rows, phaseId, onClose }: { group: Group; rows: StandingRow[]; phaseId: string; onClose: () => void }) {
  const t = useT();
  const { dispatch } = useTurnier();
  const comp = useCompetition();
  const lookup = teamLookup(comp);
  const tied = rows.filter((r) => r.needsLot);
  const [order, setOrder] = useState<string[]>(rows.map((r) => r.teamId));
  const tiedIds = new Set(tied.map((r) => r.teamId));
  const move = (i: number, d: number) => {
    const j = i + d;
    const a = order[i];
    const b = order[j];
    if (a === undefined || b === undefined || !tiedIds.has(a) || !tiedIds.has(b)) return;
    const next = order.slice();
    next[i] = b;
    next[j] = a;
    setOrder(next);
  };
  const shuffle = () => {
    const ids = order.filter((id) => tiedIds.has(id));
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j] as string, ids[i] as string];
    }
    let k = 0;
    setOrder(order.map((id) => (tiedIds.has(id) ? (ids[k++] as string) : id)));
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('Gruppe {g}: Los entscheiden', { g: group.name })}
      width="max-w-md"
      footer={
        <>
          <Button onClick={shuffle}>
            <Shuffle size={14} /> {t('Zufällig')}
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            {t('Abbrechen')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              dispatch({ type: 'group.resolveLot', phaseId, groupId: group.id, order });
              onClose();
            }}
          >
            {t('Reihenfolge festlegen')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted mt-0">{t('{n} Teams sind nach allen Kriterien gleich. Reihenfolge festlegen — wird im Protokoll als Los-Entscheidung vermerkt.', { n: tied.length })}</p>
      <ol className="m-0 p-0 list-none grid gap-1">
        {order.map((id, i) => (
          <li key={id} className={cx('flex items-center gap-2 border px-3 py-1.5 text-sm', tiedIds.has(id) ? 'border-warn bg-warn/10' : 'border-line opacity-50')}>
            <span className="font-mono text-dim w-4">{i + 1}</span>
            <span className="flex-1 font-bold">{lookup(id)?.name}</span>
            {tiedIds.has(id) && (
              <>
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)} aria-label={t('Nach oben')}>
                  <ArrowUp size={12} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)} aria-label={t('Nach unten')}>
                  <ArrowDown size={12} />
                </Button>
              </>
            )}
          </li>
        ))}
      </ol>
    </Dialog>
  );
}
